import { useTranslation } from 'react-i18next';

import { useIssueHistory, useIssueMembers, useIssueThemes } from '../../hooks/useIssueMgmt.js';
import { formatIssueTime } from '../../lib/issue-mgmt-format.js';
import { summarizeIssueHistory } from '../../lib/issue-mgmt-history.js';

/**
 * 課題の変更履歴（新しい順・保存操作ごとに1行）。誰が・いつ・何をしたかを出し、続きは「もっと見る」で
 * 次の行から読む（読む行数は毎回同じなので、何度押しても API の上限を超えない）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の変更履歴を、誰が・いつ・何をしたかの1行ずつ新しい順に見せる
 * @graph-connects useIssueHistory [calls] 変更履歴
 * @graph-connects useIssueMembers [calls] 人の表示名
 * @graph-connects useIssueThemes [calls] テーマの名前
 * @graph-connects summarizeIssueHistory [calls] 保存操作ごとの1行
 */
export function IssueHistory({ issueId, now }: { issueId: string; now: Date }) {
  const { t } = useTranslation();
  const history = useIssueHistory(issueId);
  const members = useIssueMembers();
  const themes = useIssueThemes();

  if (history.isError) {
    return <p className="text-sm text-[var(--color-error)]">{t('issueMgmt.common.loadError')}</p>;
  }
  if (history.data === undefined) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('issueMgmt.common.loading')}</p>
    );
  }
  const entries = history.data.pages.flatMap((page) => page.entries);
  const lines = summarizeIssueHistory(entries, issueId, {
    t,
    nameOf: (email) => members.data?.members.find((m) => m.email === email)?.displayName ?? email,
    themeName: (id) => themes.data?.themes.find((theme) => theme.id === id)?.name ?? id,
  });
  if (lines.length === 0) {
    return <p className="text-sm text-[var(--color-text-muted)]">{t('issueMgmt.history.empty')}</p>;
  }
  return (
    <div>
      <ul className="space-y-1.5">
        {lines.map((line) => (
          <li key={line.operationId} className="flex gap-3 text-xs">
            <span className="w-24 shrink-0 text-[var(--color-text-muted)]">
              {formatIssueTime(line.changedAt, null, now)}
            </span>
            <span className="w-28 shrink-0 truncate text-[var(--color-text-secondary)]">
              {line.actor}
            </span>
            <span className="min-w-0 flex-1 text-[var(--color-text-primary)]">{line.text}</span>
          </li>
        ))}
      </ul>
      {history.hasNextPage && (
        <button
          type="button"
          disabled={history.isFetchingNextPage}
          onClick={() => {
            void history.fetchNextPage();
          }}
          className="mt-2 text-xs text-[var(--color-accent-primary)] hover:underline"
        >
          {t('issueMgmt.common.showMore')}
        </button>
      )}
    </div>
  );
}
